<template>
  <Dialog v-model="dialogVisible" :title="dialogTitle" width="600px">
    <el-form
      ref="formRef"
      v-loading="formLoading"
      :model="formData"
      :rules="formRules"
      label-width="110px"
    >
      <el-form-item label="已选客户">
        <el-text type="primary">共 {{ formData.customerIds.length }} 个客户</el-text>
      </el-form-item>
      <el-form-item :label="isShareMode ? '共享给' : '取消共享对象'" prop="toUserIds">
        <el-select
          v-model="formData.toUserIds"
          class="w-1/1"
          collapse-tags
          collapse-tags-tooltip
          filterable
          multiple
          :placeholder="
            isShareMode ? '请选择要共享给的用户（可多选）' : '请选择要取消共享的用户（可多选）'
          "
        >
          <el-option
            v-for="item in userOptions"
            :key="item.id"
            :disabled="item.id === currentUserId"
            :label="item.nickname"
            :value="item.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item v-if="isShareMode" label="提示">
        <el-text type="info" size="small">
          被共享人可以在「别人共享给我的」列表中看到这些客户，能查看并参与跟进；如需收回，可在「我共享的」列表中取消共享。
        </el-text>
      </el-form-item>
    </el-form>
    <template #footer>
      <el-button :disabled="formLoading" type="primary" @click="submitForm">确 定</el-button>
      <el-button @click="dialogVisible = false">取 消</el-button>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import * as CustomerApi from '@/api/crm/customer'
import * as UserApi from '@/api/system/user'
import { getCurrentUserId } from '@/utils/auth'

const { t } = useI18n() // 国际化
const message = useMessage() // 消息弹窗

const dialogVisible = ref(false) // 弹窗的是否展示
const dialogTitle = ref('') // 弹窗的标题
const formLoading = ref(false) // 表单的加载中
const mode = ref<'share' | 'unshare'>('share') // 弹窗模式
const userOptions = ref<UserApi.UserVO[]>([]) // 用户列表
const currentUserId = getCurrentUserId() // 当前登录用户，不允许共享给自己
const formData = ref<{ customerIds: number[]; toUserIds: number[] }>({
  customerIds: [],
  toUserIds: []
})
const formRules = reactive({
  toUserIds: [{ required: true, message: '请选择用户', trigger: 'change' }]
})
const formRef = ref() // 表单 Ref

/** 是否「共享」模式：默认按共享处理，只有明确走取消共享时才切换 */
const isShareMode = computed(() => mode.value !== 'unshare')

/** 打开弹窗 */
const open = async (dialogMode: 'share' | 'unshare', customerIds: number[]) => {
  // ★ 先定模式，再 resetForm：resetForm 里会 resetFields，必须让 isShareMode 已经正确
  mode.value = dialogMode
  resetForm()
  formData.value.customerIds = [...customerIds]
  dialogTitle.value = dialogMode === 'share' ? '共享客户' : '取消共享'
  dialogVisible.value = true
  // 获得用户列表
  formLoading.value = true
  try {
    userOptions.value = await UserApi.getSimpleUserList()
    // 取消共享时：回显已共享的用户，便于直接勾选取消
    if (dialogMode === 'unshare') {
      const shares: CustomerApi.CustomerShareVO[] =
        (await CustomerApi.getCustomerShareList(customerIds)) || []
      formData.value.toUserIds = [...new Set(shares.map((item) => item.toUserId))]
    }
  } finally {
    formLoading.value = false
  }
}
defineExpose({ open }) // 提供 open 方法，用于打开弹窗

/** 提交表单 */
const emit = defineEmits(['success']) // 定义 success 事件，用于操作成功后的回调
const submitForm = async () => {
  if (!formRef) return
  const valid = await formRef.value.validate()
  if (!valid) return
  formLoading.value = true
  try {
    const { customerIds, toUserIds } = formData.value
    if (isShareMode.value) {
      await CustomerApi.shareCustomer({ customerIds, toUserIds })
      message.success('共享成功')
    } else {
      await CustomerApi.unshareCustomer({ customerIds, toUserIds })
      message.success('取消共享成功')
    }
    dialogVisible.value = false
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 重置表单 */
const resetForm = () => {
  formData.value = { customerIds: [], toUserIds: [] }
  formRef.value?.resetFields()
}
</script>
